import React, { useEffect, useState } from "react";
import { Plus, Trash2, AlertTriangle, Sparkles } from "lucide-react";
import { Modal, Button, Select, Checkbox, StatusBadge, LoadingBlock, toast } from "@/components/ds";
import { api, formatApiError, type ReminderConfig } from "@/lib/api";

const CHANNELS = [{ value: "sms", label: "SMS" }, { value: "voice", label: "Voice" }];

export function ReminderSettings({ onClose }: { onClose: () => void }) {
  const [cfg, setCfg] = useState<ReminderConfig | null>(null);
  const [rows, setRows] = useState<{ hours_before: number; channel: string }[]>([]);
  const [ai, setAi] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.remindersConfig().then((c) => { setCfg(c); setRows(c.reminders); }).catch((e) => toast.error(formatApiError(e)));
  }, []);

  const addRow = () => setRows((r) => [...r, { hours_before: 24, channel: "sms" }]);
  const rm = (i: number) => setRows((r) => r.filter((_, idx) => idx !== i));
  const upd = (i: number, k: string, v: unknown) => setRows((r) => r.map((row, idx) => idx === i ? { ...row, [k]: v } : row));

  const save = async () => {
    setBusy(true);
    try { await api.remindersConfigSet(rows.map((r) => ({ ...r, hours_before: Number(r.hours_before) })), ai); toast.success("Reminder sequence saved"); onClose(); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const HOURS = [2, 4, 6, 12, 24, 48, 72].map((h) => ({ value: String(h), label: `${h}h before` }));

  return (
    <Modal open onOpenChange={(o) => !o && onClose()} title="Reminder settings"
      description="A booking confirmation is always sent. Add the reminders you want before each appointment." testid="reminder-settings">
      {!cfg ? <LoadingBlock rows={3} /> : (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-2" data-testid="reminder-config-status">
            <StatusBadge tone="blue">{cfg.plan} plan</StatusBadge>
            {cfg.sms_configured ? <StatusBadge tone="green">SMS live</StatusBadge>
              : <StatusBadge tone="amber"><AlertTriangle className="mr-1 inline h-3 w-3" /> SMS: configuration required</StatusBadge>}
            {cfg.voice_available && <StatusBadge tone="slate">Voice: configuration required</StatusBadge>}
          </div>

          {!cfg.configurable ? (
            <p className="rounded-lg bg-blue-50 px-3 py-2 text-sm text-blue-700" data-testid="reminder-upgrade-note">
              Your plan includes a booking confirmation + one 24h reminder. Upgrade to Growth to add multiple configurable reminders.
            </p>
          ) : (
            <>
              <div className="space-y-2">
                {rows.map((r, i) => (
                  <div key={i} className="flex items-center gap-2" data-testid={`reminder-row-${i}`}>
                    <div className="w-40"><Select value={String(r.hours_before)} onChange={(v) => upd(i, "hours_before", Number(v))} options={HOURS} testid={`reminder-hours-${i}`} /></div>
                    <div className="w-32"><Select value={r.channel} onChange={(v) => upd(i, "channel", v)} options={cfg.voice_available ? CHANNELS : [CHANNELS[0]]} testid={`reminder-channel-${i}`} /></div>
                    <Button size="sm" variant="ghost" onClick={() => rm(i)} data-testid={`reminder-remove-${i}`}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                  </div>
                ))}
                {rows.length < cfg.max_reminders && (
                  <Button size="sm" variant="outline" onClick={addRow} data-testid="reminder-add"><Plus className="h-4 w-4" /> Add reminder</Button>
                )}
                <p className="text-[11px] text-slate-400">Up to {cfg.max_reminders} reminders on your plan.{!cfg.sms_configured && " While Twilio is dormant, due reminders are marked “Unavailable — Configuration Required” and never sent."}</p>
              </div>
              {cfg.ai_entitled && (
                <label className="flex items-center gap-2 text-sm text-slate-600">
                  <Checkbox checked={ai} onChange={(v: boolean) => setAi(v)} testid="reminder-ai-toggle" />
                  <Sparkles className="h-4 w-4 text-blue-500" /> AI-personalized reminder messages {cfg.ai_available ? "" : "(needs LLM)"}
                </label>
              )}
            </>
          )}
          <div className="flex justify-end gap-2 border-t border-slate-100 pt-4">
            <Button variant="outline" onClick={onClose}>Close</Button>
            {cfg.configurable && <Button onClick={save} disabled={busy} data-testid="reminder-save-btn">Save sequence</Button>}
          </div>
        </div>
      )}
    </Modal>
  );
}
